/**
 * La conciliación con IA, en una franja encima de la tabla.
 *
 * TRES MOMENTOS. Mientras trabaja —tarda unos minutos: piensa con toda la cartera delante—, dice
 * en qué está y cuánto lleva, y se puede seguir trabajando: al terminar la tabla se actualiza
 * sola. Al terminar, cuántos ha resuelto, cuántos ha dejado con duda y su resumen, con un botón
 * que filtra la tabla a las dudas. Y si no ha pasado todavía y queda algo pendiente, el botón
 * para pedírsela.
 *
 * LA PASADA SE SIGUE POR SU ID (`GET /ledger/ai-runs/:id`) cada pocos segundos solo mientras
 * está en curso. El mes la trae en `bank.aiRun`, así que sobrevive a recargar la página.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AiReconciliationRun, LedgerBank } from '@naar/shared';
import { ApiError, api, queryKeys } from '../../api/client';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { toast } from '../../components/feedback/Toaster';
import styles from './AiReconciliationBanner.module.css';

interface AiReconciliationBannerProps {
  month: string;
  bank: LedgerBank;
  canManage: boolean;
  /** El mes está conciliado: no se toca. */
  closed: boolean;
  /** Filtra la tabla a los movimientos con duda. */
  onShowDoubts: () => void;
}

const SONDEO_MS = 2_500;

function duracion(segundos: number): string {
  if (segundos < 60) return `${segundos} s`;
  const minutos = Math.floor(segundos / 60);
  const resto = segundos % 60;
  return resto === 0 ? `${minutos} min` : `${minutos} min ${resto} s`;
}

function plural(n: number, uno: string, varios: string): string {
  return `${n} ${n === 1 ? uno : varios}`;
}

/** La más reciente de dos pasadas: la que se acaba de lanzar puede no haber llegado aún al mes. */
function masReciente(a: AiReconciliationRun | null, b: AiReconciliationRun | null): AiReconciliationRun | null {
  if (!a) return b;
  if (!b) return a;
  return a.startedAt >= b.startedAt ? a : b;
}

/** En qué está, dicho para quien espera. */
function queHace(run: AiReconciliationRun): string {
  switch (run.phase) {
    case 'contexto':
      return 'Reuniendo la información de la cartera: inquilinos, contratos, rentas, tickets, gastos fijos e historial…';
    case 'pensando':
      return run.processed > 0
        ? `Razonando… ${run.processed} de ${run.total} revisados`
        : `Razonando sobre ${plural(run.total, 'movimiento', 'movimientos')} con toda la cartera delante…`;
    case 'aplicando':
      return `Aplicando lo que tiene claro… ${Math.min(run.total, run.applied + run.doubts)} de ${run.total}`;
    default:
      return 'Terminando…';
  }
}

/** Cuánto lleva, de 0 a 100. Mientras piensa sin haber terminado ningún lote, `null` (barra que respira). */
function avance(run: AiReconciliationRun): number | null {
  const total = Math.max(1, run.total);
  if (run.phase === 'contexto') return 4;
  if (run.phase === 'pensando') return run.processed > 0 ? 10 + (70 * run.processed) / total : null;
  if (run.phase === 'aplicando') return 80 + (20 * Math.min(total, run.applied + run.doubts)) / total;
  return 100;
}

export function AiReconciliationBanner({ month, bank, canManage, closed, onShowDoubts }: AiReconciliationBannerProps) {
  const queryClient = useQueryClient();
  const [lanzada, setLanzada] = useState<AiReconciliationRun | null>(null);
  const [cerrada, setCerrada] = useState<string | null>(null);

  // Al cambiar de mes, lo lanzado en otro no vale.
  useEffect(() => setLanzada(null), [month]);

  const base = masReciente(lanzada, bank.aiRun);
  const enCurso = base?.status === 'en_curso';
  const { data: sondeada } = useQuery({
    queryKey: queryKeys.aiRun(base?.id ?? 'ninguna'),
    queryFn: () => api.aiReconciliationRun(base!.id),
    enabled: Boolean(base) && enCurso,
    refetchInterval: (query) => (query.state.data?.status === 'en_curso' || !query.state.data ? SONDEO_MS : false),
  });
  const run = base && sondeada?.id === base.id ? masRecienteEstado(base, sondeada) : base;

  /*
   * Al terminar: la tabla se recarga (lo resuelto deja de estar pendiente, aparecen las dudas) y
   * se avisa, porque quien la lanzó probablemente estaba mirando otra cosa.
   */
  const anterior = useRef<{ id: string; status: string } | null>(null);
  useEffect(() => {
    if (!run) return;
    const antes = anterior.current;
    anterior.current = { id: run.id, status: run.status };
    if (!antes || antes.id !== run.id || antes.status !== 'en_curso' || run.status === 'en_curso') return;
    void queryClient.invalidateQueries({ queryKey: ['ledger'] });
    void queryClient.invalidateQueries({ queryKey: ['rent-charges'] });
    if (run.status === 'terminado') {
      toast.success(
        `La IA ha terminado: ${plural(run.applied, 'movimiento resuelto', 'movimientos resueltos')}${
          run.doubts > 0 ? ` y ${plural(run.doubts, 'duda', 'dudas')} para revisar` : ''
        }.`,
      );
    } else if (run.status === 'fallido') {
      toast.error(run.error ?? 'La IA no ha podido terminar.');
    }
  }, [run, queryClient]);

  const lanzar = useMutation({
    mutationFn: () => api.startAiReconciliation(month),
    onSuccess: async ({ run: nueva }) => {
      if (!nueva) {
        toast.info('No queda nada pendiente en este mes.');
        return;
      }
      setLanzada(nueva);
      setCerrada(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.ledgerMonth(month) });
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido lanzar la IA.'),
  });

  /*
   * «Volver a identificar»: repasa con las reglas TODOS los ingresos pendientes, de todos los
   * meses, con los datos de ahora. Es lo que se pulsa tras dar de alta al inquilino que faltaba:
   * es inmediato y no gasta IA.
   */
  const reidentificar = useMutation({
    mutationFn: () => api.retryIdentification(),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      await queryClient.invalidateQueries({ queryKey: ['rent-charges'] });
      if (result.identified === 0) {
        toast.info('No he encontrado nada nuevo con las reglas. Prueba a pasar la IA.');
      } else {
        toast.success(
          `${plural(result.identified, 'ingreso identificado', 'ingresos identificados')}${
            result.pending > 0 ? `; quedan ${result.pending} pendientes en total` : ''
          }.`,
        );
      }
    },
    onError: (error: unknown) =>
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido volver a identificar.'),
  });

  if (!bank.aiAvailable) return null;

  const pendientes = bank.movements.filter((movement) => movement.status === 'pendiente');
  const ingresosPendientes = pendientes.some((movement) => movement.kind === 'ingreso');
  const botonIdentificar =
    canManage && !closed && ingresosPendientes ? (
      <Button variant="secondary" size="sm" onClick={() => reidentificar.mutate()} disabled={reidentificar.isPending}>
        {reidentificar.isPending ? 'Buscando…' : 'Volver a identificar ingresos'}
      </Button>
    ) : null;
  const dudas = pendientes.filter((movement) => movement.aiDoubt).length;
  const puedeLanzar = canManage && !closed && pendientes.length > 0;
  const botonLanzar = (texto: string) => (
    <Button variant="secondary" size="sm" onClick={() => lanzar.mutate()} disabled={lanzar.isPending}>
      <Icon name="bolt" size={14} strokeWidth={2.2} />
      {lanzar.isPending ? 'Lanzando…' : texto}
    </Button>
  );

  // --- Trabajando ----------------------------------------------------------
  if (run?.status === 'en_curso') {
    const pct = avance(run);
    return (
      <section className={`${styles.banner} ${styles.trabajando}`} aria-live="polite" aria-busy="true">
        <span className={styles.icono} aria-hidden="true">
          <Icon name="bolt" size={16} strokeWidth={2.2} />
        </span>
        <div className={styles.cuerpo}>
          <div className={styles.titulo}>
            <strong>La IA está conciliando {plural(run.total, 'movimiento pendiente', 'movimientos pendientes')}</strong>
            <span className={styles.meta}>{duracion(run.elapsedSeconds)}</span>
          </div>
          <p className={styles.texto}>{queHace(run)}</p>
          <div
            className={styles.barra}
            role="progressbar"
            aria-label="Avance de la conciliación con IA"
            {...(pct === null ? {} : { 'aria-valuenow': Math.round(pct), 'aria-valuemin': 0, 'aria-valuemax': 100 })}
          >
            <span
              className={pct === null ? `${styles.relleno} ${styles.respira}` : styles.relleno}
              style={pct === null ? undefined : { width: `${pct}%` }}
            />
          </div>
          <p className={styles.nota}>
            Piensa con calma y puede tardar unos minutos. Puedes seguir trabajando: al terminar, la tabla se actualiza sola.
          </p>
        </div>
      </section>
    );
  }

  // --- Terminada -----------------------------------------------------------
  if (run && run.status === 'terminado' && cerrada !== run.id) {
    return (
      <section className={`${styles.banner} ${styles.hecha}`}>
        <span className={styles.icono} aria-hidden="true">
          <Icon name="bolt" size={16} strokeWidth={2.2} />
        </span>
        <div className={styles.cuerpo}>
          <div className={styles.titulo}>
            <strong>
              La IA ha resuelto {run.applied} de {plural(run.total, 'movimiento', 'movimientos')}
              {run.doubts > 0 ? ` y ha dejado ${plural(run.doubts, 'duda', 'dudas')}` : ''}
            </strong>
            <span className={styles.meta}>
              {run.model} · {duracion(run.elapsedSeconds)}
            </span>
          </div>
          {run.summary && <p className={styles.texto}>{run.summary}</p>}
          {run.error && <p className={styles.aviso}>{run.error}</p>}
          <p className={styles.nota}>
            Lo resuelto lleva la marca <span className={styles.chip}>IA</span> con su porqué, y «Volver a pendiente» lo
            deshace. Las dudas siguen pendientes con su propuesta ya elegida al abrirlas.
          </p>
          {(dudas > 0 || puedeLanzar) && (
            <div className={styles.acciones}>
              {dudas > 0 && (
                <Button size="sm" onClick={onShowDoubts}>
                  Ver {plural(dudas, 'duda', 'dudas')}
                </Button>
              )}
              {botonIdentificar}
              {puedeLanzar && botonLanzar('Volver a pasar la IA')}
            </div>
          )}
        </div>
        <button
          type="button"
          className={styles.cerrar}
          onClick={() => setCerrada(run.id)}
          aria-label="Ocultar el resultado de la IA"
          title="Ocultar"
        >
          <Icon name="close" size={14} strokeWidth={2.2} />
        </button>
      </section>
    );
  }

  // --- Falló o se interrumpió ----------------------------------------------
  if (run && (run.status === 'fallido' || run.status === 'interrumpido') && cerrada !== run.id) {
    return (
      <section className={`${styles.banner} ${styles.fallo}`} role="status">
        <span className={styles.icono} aria-hidden="true">
          <Icon name="info" size={16} strokeWidth={2.2} />
        </span>
        <div className={styles.cuerpo}>
          <div className={styles.titulo}>
            <strong>{run.status === 'fallido' ? 'La IA no ha podido conciliar' : 'La conciliación con IA se interrumpió'}</strong>
          </div>
          <p className={styles.texto}>
            {run.error ?? 'Sin motivo.'} Lo que dejaron resuelto las reglas sigue igual.
          </p>
          {puedeLanzar && <div className={styles.acciones}>{botonLanzar('Reintentar')}</div>}
        </div>
        <button type="button" className={styles.cerrar} onClick={() => setCerrada(run.id)} aria-label="Ocultar" title="Ocultar">
          <Icon name="close" size={14} strokeWidth={2.2} />
        </button>
      </section>
    );
  }

  // --- Sin pasada y con pendientes -----------------------------------------
  if (puedeLanzar && (!run || cerrada === run.id || run.status === 'sustituido')) {
    return (
      <section className={`${styles.banner} ${styles.invita}`}>
        <span className={styles.icono} aria-hidden="true">
          <Icon name="bolt" size={16} strokeWidth={2.2} />
        </span>
        <div className={styles.cuerpo}>
          <p className={styles.texto}>
            <strong>{plural(pendientes.length, 'movimiento pendiente', 'movimientos pendientes')}.</strong>{' '}
            {dudas > 0
              ? `${plural(dudas, 'tiene', 'tienen')} duda de la IA. Si has dado de alta a alguien o subido tickets, vuelve a pasarla.`
              : 'La IA puede revisarlos con toda la información de la cartera y dejarte solo las dudas.'}
          </p>
        </div>
        <div className={styles.accionesLado}>
          {botonIdentificar}
          {botonLanzar(dudas > 0 ? 'Volver a pasar la IA' : 'Conciliar con IA')}
        </div>
      </section>
    );
  }

  return null;
}

/** De la misma pasada, la foto con más avance: la del sondeo, salvo que la del mes sea posterior. */
function masRecienteEstado(delMes: AiReconciliationRun, sondeada: AiReconciliationRun): AiReconciliationRun {
  if (delMes.status !== 'en_curso' && sondeada.status === 'en_curso') return delMes;
  return sondeada;
}
